import { useEffect, useState, useMemo } from "react";
import {
  FiExternalLink,
  FiCalendar,
  FiTrendingUp,
  FiRefreshCw,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiFilter,
  FiX,
} from "react-icons/fi";
import { toast } from "sonner";
import { SEO } from "@/components/seo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CardSkeleton } from "@/components/common";
import { useUser } from "@/features/auth";

type RbaNews = {
  id: string;
  title: string;
  link: string;
  description: string | null;
  category: string | null;
  imageUrl: string | null;
  pubDate: string;
};

const ITEMS_PER_PAGE = 12;

// Main source categories (RBA official sources only)
const SOURCE_CATEGORIES = [
  "RBA Media",
  "RBA Speeches",
  "RBA Bulletin",
];

type SortOption = "newest" | "oldest";

// Category color mapping
function getCategoryColor(category: string | null) {
  if (category === "RBA Media") return "bg-blue-100 text-blue-700";
  if (category === "RBA Speeches") return "bg-purple-100 text-purple-700";
  if (category === "RBA Bulletin") return "bg-indigo-100 text-indigo-700";
  return "bg-muted text-muted-foreground";
}

export function MarketNewsPage() {
  const [news, setNews] = useState<RbaNews[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSource, setSelectedSource] = useState("All");
  const [sortOrder, setSortOrder] = useState<SortOption>("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const user = useUser();
  const isAdmin = user?.type === "admin";

  // Filter and sort news
  const filteredNews = useMemo(() => {
    const filtered = news.filter((item) => {
      const matchesSearch =
        !searchQuery ||
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSource =
        selectedSource === "All" || item.category === selectedSource;
      return matchesSearch && matchesSource;
    });

    // Sort by date
    return filtered.sort((a, b) => {
      const dateA = new Date(a.pubDate).getTime();
      const dateB = new Date(b.pubDate).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [news, searchQuery, selectedSource, sortOrder]);

  // Paginated news
  const totalPages = Math.ceil(filteredNews.length / ITEMS_PER_PAGE);
  const paginatedNews = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredNews.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredNews, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedSource, sortOrder]);

  const hasFilters = searchQuery || selectedSource !== "All" || sortOrder !== "newest";

  function clearFilters() {
    setSearchQuery("");
    setSelectedSource("All");
    setSortOrder("newest");
  }

  async function fetchNews() {
    try {
      const res = await fetch("/api/market/rba-news");
      const data = await res.json();
      setNews(data as RbaNews[]);
    } catch (err) {
      console.error("Failed to fetch news:", err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchNews();
  }, []);

  async function handleSync(clear = false) {
    setIsSyncing(true);
    try {
      const endpoint = clear ? "/api/admin/market/resync" : "/api/admin/market/sync";
      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
      });
      const data = (await res.json()) as { message?: string; error?: string };
      if (res.ok) {
        toast.success(data.message || "Sync complete");
        await fetchNews();
      } else {
        toast.error(data.error || "Sync failed");
      }
    } catch {
      toast.error("Failed to sync");
    } finally {
      setIsSyncing(false);
    }
  }

  return (
    <>
      <SEO
        title="Market News & RBA Updates"
        description="Stay informed with the latest Reserve Bank of Australia news, interest rate decisions, and market updates affecting home loans and mortgages."
        url="/market-news"
      />

      {/* Hero */}
      <section className="bg-primary text-primary-foreground py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 text-primary-foreground/90 mb-4">
                <FiTrendingUp className="w-5 h-5" />
                <span className="text-sm font-medium">Market Updates</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-medium mb-4">
                RBA News & Updates
              </h1>
              <p className="text-lg text-primary-foreground/80">
                Latest media releases and announcements from the Reserve Bank of
                Australia that may impact interest rates and home loans.
              </p>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button
                  onClick={() => handleSync(false)}
                  disabled={isSyncing}
                  variant="secondary"
                  size="sm"
                >
                  <FiRefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? "animate-spin" : ""}`} />
                  {isSyncing ? "Syncing..." : "Sync"}
                </Button>
                <Button
                  onClick={() => handleSync(true)}
                  disabled={isSyncing}
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/20 hover:bg-white/20"
                >
                  Clear & Resync
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Filters & News List */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            {/* Search */}
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-white"
              />
            </div>

            {/* Source Filter */}
            <div className="relative">
              <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <select
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
                className="appearance-none pl-10 pr-8 py-2.5 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-white cursor-pointer"
              >
                <option value="All">All Sources</option>
                {SOURCE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" />
            </div>

            {/* Sort Order */}
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOption)}
                className="appearance-none pl-10 pr-8 py-2.5 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-white cursor-pointer"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
              <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" />
            </div>

            {/* Clear Filters */}
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <FiX className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Results count */}
          {!isLoading && (
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {filteredNews.length} {filteredNews.length === 1 ? "article" : "articles"}
                {selectedSource !== "All" && ` from ${selectedSource}`}
              </p>
              {totalPages > 1 && (
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </p>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : filteredNews.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FiTrendingUp className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {news.length === 0
                    ? "No news available. Click 'Sync RBA Feed' to fetch the latest news."
                    : "No articles match your filters."}
                </p>
                {news.length > 0 && hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 text-sm text-primary hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* News Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedNews.map((item) => (
                  <a
                    key={item.id}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden">
                      {/* Cover Image */}
                      {item.imageUrl && (
                        <div className="aspect-video bg-muted overflow-hidden">
                          <img
                            src={item.imageUrl}
                            alt=""
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        </div>
                      )}
                      <CardContent className={`p-5 ${!item.imageUrl ? "pt-5" : ""}`}>
                        {/* Category & Date */}
                        <div className="flex items-center gap-2 mb-3">
                          {item.category && (
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(
                                item.category
                              )}`}
                            >
                              {item.category}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <FiCalendar className="w-3 h-3" />
                            {new Date(item.pubDate).toLocaleDateString("en-AU", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>

                        {/* Title */}
                        <h2 className="font-medium text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
                          {item.title}
                        </h2>

                        {/* Description */}
                        {item.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                            {item.description}
                          </p>
                        )}

                        {/* Read more */}
                        <div className="flex items-center gap-1 text-sm font-medium text-primary group-hover:underline">
                          Read more
                          <FiExternalLink className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <FiChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <FiChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Disclaimer */}
          <p className="text-xs text-muted-foreground text-center mt-10">
            News sourced from the Reserve Bank of Australia and other financial news providers.
            Content is provided for informational purposes only.
          </p>
        </div>
      </section>
    </>
  );
}
